<template>
  <main>
    <div class="game-container">
      <div class="game-left-column">
        <div class="board-section">
          <div class="captured-section white-captured">
            <p class="captured-label">White Captured</p>
            <div class="captured-list">
              <span v-for="(piece, index) in capturedWhitePieces" :key="`white-${index}`" class="captured-piece">
                {{ getPieceSymbol(piece) }}
              </span>
            </div>
          </div>
          <div class="canvas-wrapper">
            <canvas
              ref="gameCanvas"
              class="game-canvas"
              @click="handleCanvasClick"
              @touchstart="handleCanvasTouch"
            ></canvas>
          </div>
          <div class="captured-section black-captured">
            <p class="captured-label">Black Captured</p>
            <div class="captured-list">
              <span v-for="(piece, index) in capturedBlackPieces" :key="`black-${index}`" class="captured-piece">
                {{ getPieceSymbol(piece) }}
              </span>
            </div>
          </div>
          <div class="instructions">
            <p><strong>Controls:</strong> Click to select and move pieces</p>
            <p>White plays first. Click a piece to select it, then click a square to move.</p>
          </div>
        </div>
      </div>
      <div class="game-right-column">
        <div class="game-controls-panel">
          <div class="game-buttons">
            <button v-if="!gameStarted" @click="startGame" class="btn btn-primary">Start Game</button>
            <button v-else @click="resetGame" class="btn btn-secondary">New Game</button>
          </div>
          <div class="game-status">
            <p v-if="!gameStarted" class="not-started-warning">Game Not Started</p>
            <p v-else-if="timeForfeit" class="game-over-warning">{{ currentPlayer === 'white' ? 'White' : 'Black' }} lost on time! {{ winner }} wins!</p>
            <p v-else-if="gameOver" class="game-over-warning">Game Over! {{ winner }} wins!</p>
            <p v-else-if="currentPlayer === 'white'" class="status">White's Turn</p>
            <p v-else class="status">Black's Turn</p>
            <p v-if="checkStatus && !gameOver && gameStarted" class="check-warning">⚠️ Check!</p>
            <p v-if="checkmate" class="checkmate-warning">Checkmate! {{ currentPlayer === 'white' ? 'Black' : 'White' }} wins!</p>
          </div>
        </div>
        <div class="clocks-panel">
          <div class="clock white-clock" :class="{ active: gameStarted && currentPlayer === 'white' && !gameOver }">
            <p class="clock-label">White</p>
            <p class="clock-time">{{ formatTime(whiteTime) }}</p>
          </div>
          <div class="clock black-clock" :class="{ active: gameStarted && currentPlayer === 'black' && !gameOver }">
            <p class="clock-label">Black</p>
            <p class="clock-time">{{ formatTime(blackTime) }}</p>
          </div>
        </div>
        <div class="moves-panel">
          <h3>Move History</h3>
          <ul class="moves-list">
            <li v-for="(fullMove, index) in groupedMoves" :key="index" class="move-entry">
              <span class="move-number">{{ index + 1 }}.</span>
              <span class="white-move">{{ fullMove.white }}</span>
              <span class="black-move">{{ fullMove.black || '...' }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
export default {
  name: 'ChessGame',
  data() {
    return {
      canvasSize: 400,
      squareSize: 50,
      board: [],
      selectedSquare: null,
      validMoves: [],
      currentPlayer: 'white',
      checkStatus: false,
      checkmate: false,
      whiteKingPos: { x: 4, y: 7 },
      blackKingPos: { x: 4, y: 0 },
      gameOver: false,
      winner: null,
      moves: [],
      whiteTime: 5 * 60,
      blackTime: 5 * 60,
      clockInterval: null,
      gameStarted: false,
      capturedWhitePieces: [],
      capturedBlackPieces: [],
      timeForfeit: false
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.resizeCanvas();
      this.initBoard();
      this.draw();
    });
    window.addEventListener('resize', () => this.resizeCanvas());
  },
  beforeDestroy() {
    window.removeEventListener('resize', () => this.resizeCanvas());
    this.stopClock();
  },
  computed: {
    groupedMoves() {
      const grouped = [];
      for (let i = 0; i < this.moves.length; i += 2) {
        grouped.push({
          white: this.moves[i],
          black: this.moves[i + 1] || null
        });
      }
      return grouped;
    }
  },
  methods: {
    resizeCanvas() {
      this.$nextTick(() => {
        const canvas = this.$refs.gameCanvas;
        if (!canvas) return;

        const container = canvas.parentElement;

        // On mobile landscape, prioritize width constraint to prevent extreme aspect ratios
        let maxSize;
        if (window.innerWidth <= 768) {
          // Mobile: use smaller of available width or a landscape-safe height
          const widthBased = container.clientWidth - 20;
          const heightBased = Math.min(window.innerHeight * 0.5, 300);
          maxSize = Math.min(widthBased, heightBased, 400);
        } else {
          // Tablet/Desktop: more generous height allowance
          maxSize = Math.min(
            container.clientWidth - 20,
            window.innerHeight * 0.6,
            400
          );
        }

        canvas.width = maxSize;
        canvas.height = maxSize;
        this.canvasSize = maxSize;
        this.squareSize = maxSize / 8;
        this.draw();
      });
    },
    initBoard() {
      this.board = Array(8).fill(null).map(() => Array(8).fill(null));

      // White pieces
      this.board[7][0] = { type: 'rook', color: 'white' };
      this.board[7][1] = { type: 'knight', color: 'white' };
      this.board[7][2] = { type: 'bishop', color: 'white' };
      this.board[7][3] = { type: 'queen', color: 'white' };
      this.board[7][4] = { type: 'king', color: 'white' };
      this.board[7][5] = { type: 'bishop', color: 'white' };
      this.board[7][6] = { type: 'knight', color: 'white' };
      this.board[7][7] = { type: 'rook', color: 'white' };

      for (let i = 0; i < 8; i++) {
        this.board[6][i] = { type: 'pawn', color: 'white' };
      }

      // Black pieces
      this.board[0][0] = { type: 'rook', color: 'black' };
      this.board[0][1] = { type: 'knight', color: 'black' };
      this.board[0][2] = { type: 'bishop', color: 'black' };
      this.board[0][3] = { type: 'queen', color: 'black' };
      this.board[0][4] = { type: 'king', color: 'black' };
      this.board[0][5] = { type: 'bishop', color: 'black' };
      this.board[0][6] = { type: 'knight', color: 'black' };
      this.board[0][7] = { type: 'rook', color: 'black' };

      for (let i = 0; i < 8; i++) {
        this.board[1][i] = { type: 'pawn', color: 'black' };
      }
    },
    handleCanvasClick(event) {
      if (!this.gameStarted || this.checkmate || this.gameOver) return;

      const canvas = this.$refs.gameCanvas;
      const rect = canvas.getBoundingClientRect();

      // Calculate scale factor: visual size vs actual canvas size
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      // Get coordinates accounting for scaling
      const x = Math.floor((event.clientX - rect.left) * scaleX / this.squareSize);
      const y = Math.floor((event.clientY - rect.top) * scaleY / this.squareSize);

      if (x < 0 || x > 7 || y < 0 || y > 7) return;

      if (this.selectedSquare && this.validMoves.some(m => m.x === x && m.y === y)) {
        this.movePiece(this.selectedSquare, { x, y });
        this.selectedSquare = null;
        this.validMoves = [];
        this.switchTurn();
        this.checkStatus = this.isInCheck(this.currentPlayer);
        this.checkmate = this.isCheckmate(this.currentPlayer);
      } else if (this.board[y][x] && this.board[y][x].color === this.currentPlayer) {
        this.selectedSquare = { x, y };
        this.validMoves = this.getValidMoves({ x, y });
      } else {
        this.selectedSquare = null;
        this.validMoves = [];
      }

      this.draw();
    },
    handleCanvasTouch(event) {
      event.preventDefault();
      const touch = event.touches[0];

      if (!this.gameStarted || this.checkmate || this.gameOver) return;

      const canvas = this.$refs.gameCanvas;
      const rect = canvas.getBoundingClientRect();

      // Calculate scale factor: visual size vs actual canvas size
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      // Get coordinates accounting for scaling
      const x = Math.floor((touch.clientX - rect.left) * scaleX / this.squareSize);
      const y = Math.floor((touch.clientY - rect.top) * scaleY / this.squareSize);

      if (x < 0 || x > 7 || y < 0 || y > 7) return;

      if (this.selectedSquare && this.validMoves.some(m => m.x === x && m.y === y)) {
        this.movePiece(this.selectedSquare, { x, y });
        this.selectedSquare = null;
        this.validMoves = [];
        this.switchTurn();
        this.checkStatus = this.isInCheck(this.currentPlayer);
        this.checkmate = this.isCheckmate(this.currentPlayer);
      } else if (this.board[y][x] && this.board[y][x].color === this.currentPlayer) {
        this.selectedSquare = { x, y };
        this.validMoves = this.getValidMoves({ x, y });
      } else {
        this.selectedSquare = null;
        this.validMoves = [];
      }

      this.draw();
    },
    coordsToChessNotation(x, y) {
      const file = String.fromCharCode(97 + x); // a-h
      const rank = 8 - y; // 8-1
      return file + rank;
    },
    getPieceSymbol(piece) {
      const symbols = {
        pawn: '♙',
        rook: '♖',
        knight: '♘',
        bishop: '♗',
        queen: '♕',
        king: '♔'
      };
      const blackSymbols = {
        pawn: '♟',
        rook: '♜',
        knight: '♞',
        bishop: '♝',
        queen: '♛',
        king: '♚'
      };
      return piece.color === 'white' ? symbols[piece.type] : blackSymbols[piece.type];
    },
    generateSAN(from, to, piece, capturedPiece) {
      const toNotation = this.coordsToChessNotation(to.x, to.y);
      let moveStr = '';

      // Add piece letter (no letter for pawns)
      if (piece.type !== 'pawn') {
        const pieceLetter = {
          knight: 'N',
          bishop: 'B',
          rook: 'R',
          queen: 'Q',
          king: 'K'
        };
        moveStr += pieceLetter[piece.type];
      }

      // Add capture notation
      if (capturedPiece) {
        if (piece.type === 'pawn') {
          // For pawn captures, add the file letter
          moveStr += this.coordsToChessNotation(from.x, from.y)[0];
        }
        moveStr += 'x';
      }

      // Add destination square
      moveStr += toNotation;

      // Add check or checkmate notation
      if (this.checkmate) {
        moveStr += '#';
      } else if (this.checkStatus) {
        moveStr += '+';
      }

      return moveStr;
    },
    recordMove(from, to, piece, capturedPiece) {
      const san = this.generateSAN(from, to, piece, capturedPiece);
      const symbol = this.getPieceSymbol(piece);
      const fromNotation = this.coordsToChessNotation(from.x, from.y);
      const toNotation = this.coordsToChessNotation(to.x, to.y);
      const moveText = `${symbol} ${san} (${fromNotation}–${toNotation})`;
      this.moves.push(moveText);
    },
    movePiece(from, to) {
      const piece = this.board[from.y][from.x];
      const capturedPiece = this.board[to.y][to.x];

      // Check if a king is being captured
      if (capturedPiece && capturedPiece.type === 'king') {
        this.gameOver = true;
        this.winner = piece.color === 'white' ? 'White' : 'Black';
      }

      // Track captured pieces
      if (capturedPiece) {
        if (capturedPiece.color === 'white') {
          this.capturedWhitePieces.push(capturedPiece);
        } else {
          this.capturedBlackPieces.push(capturedPiece);
        }
      }

      this.board[to.y][to.x] = piece;
      this.board[from.y][from.x] = null;

      if (piece.type === 'king') {
        if (piece.color === 'white') {
          this.whiteKingPos = to;
        } else {
          this.blackKingPos = to;
        }
      }

      // Record the move
      this.recordMove(from, to, piece, capturedPiece);
    },
    getValidMoves(pos) {
      const piece = this.board[pos.y][pos.x];
      if (!piece) return [];

      const moves = [];

      switch (piece.type) {
        case 'pawn':
          moves.push(...this.getPawnMoves(pos, piece.color));
          break;
        case 'rook':
          moves.push(...this.getRookMoves(pos, piece.color));
          break;
        case 'knight':
          moves.push(...this.getKnightMoves(pos, piece.color));
          break;
        case 'bishop':
          moves.push(...this.getBishopMoves(pos, piece.color));
          break;
        case 'queen':
          moves.push(...this.getQueenMoves(pos, piece.color));
          break;
        case 'king':
          moves.push(...this.getKingMoves(pos, piece.color));
          break;
      }

      return moves.filter(move => {
        const targetPiece = this.board[move.y][move.x];
        return !targetPiece || targetPiece.color !== piece.color;
      });
    },
    getPawnMoves(pos, color) {
      const moves = [];
      const direction = color === 'white' ? -1 : 1;
      const startRow = color === 'white' ? 6 : 1;

      const forward = { x: pos.x, y: pos.y + direction };
      if (forward.y >= 0 && forward.y < 8 && !this.board[forward.y][forward.x]) {
        moves.push(forward);
      }

      if (pos.y === startRow) {
        const doubleForward = { x: pos.x, y: pos.y + 2 * direction };
        if (!this.board[doubleForward.y][doubleForward.x] && !this.board[forward.y][forward.x]) {
          moves.push(doubleForward);
        }
      }

      for (let dx of [-1, 1]) {
        const capture = { x: pos.x + dx, y: pos.y + direction };
        if (capture.x >= 0 && capture.x < 8 && capture.y >= 0 && capture.y < 8) {
          if (this.board[capture.y][capture.x]) {
            moves.push(capture);
          }
        }
      }

      return moves;
    },
    getRookMoves(pos, color) {
      return this.getStraightMoves(pos, color);
    },
    getStraightMoves(pos, color) {
      const moves = [];
      const directions = [[0, 1], [1, 0], [0, -1], [-1, 0]];

      for (let dir of directions) {
        for (let i = 1; i < 8; i++) {
          const x = pos.x + dir[0] * i;
          const y = pos.y + dir[1] * i;
          if (x < 0 || x > 7 || y < 0 || y > 7) break;

          if (this.board[y][x]) {
            if (this.board[y][x].color !== color) moves.push({ x, y });
            break;
          }
          moves.push({ x, y });
        }
      }
      return moves;
    },
    getBishopMoves(pos, color) {
      return this.getDiagonalMoves(pos, color);
    },
    getDiagonalMoves(pos, color) {
      const moves = [];
      const directions = [[1, 1], [1, -1], [-1, 1], [-1, -1]];

      for (let dir of directions) {
        for (let i = 1; i < 8; i++) {
          const x = pos.x + dir[0] * i;
          const y = pos.y + dir[1] * i;
          if (x < 0 || x > 7 || y < 0 || y > 7) break;

          if (this.board[y][x]) {
            if (this.board[y][x].color !== color) moves.push({ x, y });
            break;
          }
          moves.push({ x, y });
        }
      }
      return moves;
    },
    getKnightMoves(pos, color) {
      const moves = [];
      const offsets = [
        [-2, -1], [-2, 1], [-1, -2], [-1, 2],
        [1, -2], [1, 2], [2, -1], [2, 1]
      ];

      for (let offset of offsets) {
        const x = pos.x + offset[0];
        const y = pos.y + offset[1];
        if (x >= 0 && x < 8 && y >= 0 && y < 8) {
          moves.push({ x, y });
        }
      }
      return moves;
    },
    getQueenMoves(pos, color) {
      return [...this.getStraightMoves(pos, color), ...this.getDiagonalMoves(pos, color)];
    },
    getKingMoves(pos, color) {
      const moves = [];
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          if (dx === 0 && dy === 0) continue;
          const x = pos.x + dx;
          const y = pos.y + dy;
          if (x >= 0 && x < 8 && y >= 0 && y < 8) {
            moves.push({ x, y });
          }
        }
      }
      return moves;
    },
    isInCheck(color) {
      const kingPos = color === 'white' ? this.whiteKingPos : this.blackKingPos;
      const opponentColor = color === 'white' ? 'black' : 'white';

      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          const piece = this.board[y][x];
          if (piece && piece.color === opponentColor) {
            const moves = this.getValidMoves({ x, y });
            if (moves.some(m => m.x === kingPos.x && m.y === kingPos.y)) {
              return true;
            }
          }
        }
      }
      return false;
    },
    isCheckmate(color) {
      if (!this.isInCheck(color)) return false;

      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          const piece = this.board[y][x];
          if (piece && piece.color === color) {
            const moves = this.getValidMoves({ x, y });
            for (let move of moves) {
              const backup = this.board[move.y][move.x];
              this.board[move.y][move.x] = piece;
              this.board[y][x] = null;

              if (piece.type === 'king') {
                const kingPos = color === 'white' ? this.whiteKingPos : this.blackKingPos;
                if (color === 'white') this.whiteKingPos = move;
                else this.blackKingPos = move;
              }

              const stillInCheck = this.isInCheck(color);

              this.board[y][x] = piece;
              this.board[move.y][move.x] = backup;

              if (piece.type === 'king') {
                if (color === 'white') this.whiteKingPos = { x, y };
                else this.blackKingPos = { x, y };
              }

              if (!stillInCheck) return false;
            }
          }
        }
      }
      return true;
    },
    resetGame() {
      this.stopClock();
      this.gameStarted = false;
      this.selectedSquare = null;
      this.validMoves = [];
      this.currentPlayer = 'white';
      this.checkStatus = false;
      this.checkmate = false;
      this.gameOver = false;
      this.winner = null;
      this.moves = [];
      this.whiteTime = 5 * 60;
      this.blackTime = 5 * 60;
      this.whiteKingPos = { x: 4, y: 7 };
      this.blackKingPos = { x: 4, y: 0 };
      this.capturedWhitePieces = [];
      this.capturedBlackPieces = [];
      this.timeForfeit = false;
      this.initBoard();
      this.draw();
    },
    startGame() {
      this.gameStarted = true;
      this.startClock();
    },
    startClock() {
      if (this.clockInterval) return;
      this.clockInterval = setInterval(() => {
        if (this.gameOver) {
          this.stopClock();
          return;
        }
        if (this.currentPlayer === 'white') {
          this.whiteTime--;
          if (this.whiteTime <= 0 && !this.timeForfeit) {
            this.handleTimeForfeit('white');
          }
        } else {
          this.blackTime--;
          if (this.blackTime <= 0 && !this.timeForfeit) {
            this.handleTimeForfeit('black');
          }
        }
      }, 1000);
    },
    stopClock() {
      if (this.clockInterval) {
        clearInterval(this.clockInterval);
        this.clockInterval = null;
      }
    },
    handleTimeForfeit(color) {
      this.timeForfeit = true;
      this.gameOver = true;
      this.winner = color === 'white' ? 'Black' : 'White';
      this.stopClock();
    },
    switchTurn() {
      this.currentPlayer = this.currentPlayer === 'white' ? 'black' : 'white';
    },
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    },
    draw() {
      const canvas = this.$refs.gameCanvas;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');

      // Reset canvas state
      ctx.globalAlpha = 1;
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      // Draw board
      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          const isLight = (x + y) % 2 === 0;
          ctx.fillStyle = isLight ? '#f0d9b5' : '#b58863';
          ctx.fillRect(x * this.squareSize, y * this.squareSize, this.squareSize, this.squareSize);
        }
      }

      // Highlight selected square
      if (this.selectedSquare) {
        ctx.fillStyle = 'rgba(186, 202, 43, 0.7)';
        ctx.fillRect(
          this.selectedSquare.x * this.squareSize,
          this.selectedSquare.y * this.squareSize,
          this.squareSize,
          this.squareSize
        );
      }

      // Highlight valid moves
      for (let move of this.validMoves) {
        ctx.fillStyle = 'rgba(100, 200, 100, 0.5)';
        ctx.beginPath();
        ctx.arc(
          move.x * this.squareSize + this.squareSize / 2,
          move.y * this.squareSize + this.squareSize / 2,
          this.squareSize / 6,
          0,
          2 * Math.PI
        );
        ctx.fill();
      }

      // Draw pieces
      for (let y = 0; y < 8; y++) {
        for (let x = 0; x < 8; x++) {
          const piece = this.board[y][x];
          if (piece) {
            this.drawPiece(ctx, piece, x, y);
          }
        }
      }
    },
    drawPiece(ctx, piece, x, y) {
      const centerX = x * this.squareSize + this.squareSize / 2;
      const centerY = y * this.squareSize + this.squareSize / 2;

      // Explicitly set text properties before drawing
      ctx.globalAlpha = 1;
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${this.squareSize * 0.85}px "Chess Symbols", Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;

      const whiteSymbols = {
        pawn: '♙\uFE0E',
        rook: '♖\uFE0E',
        knight: '♘\uFE0E',
        bishop: '♗\uFE0E',
        queen: '♕\uFE0E',
        king: '♔\uFE0E'
      };

      const blackSymbols = {
        pawn: '♟\uFE0E',
        rook: '♜\uFE0E',
        knight: '♞\uFE0E',
        bishop: '♝\uFE0E',
        queen: '♛\uFE0E',
        king: '♚\uFE0E'
      };

      const symbols = piece.color === 'white' ? whiteSymbols : blackSymbols;
      ctx.fillText(symbols[piece.type], centerX, centerY);
    }
  }
};
</script>

<style scoped>
main {
  padding: 10px;
}

.game-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  flex-wrap: wrap;
  width: 100%;
  min-height: 0;
}

.captured-section {
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 8px 12px;
  background-color: #f5f5f5;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  min-height: 60px;
}

.captured-label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: bold;
  color: #333;
  white-space: nowrap;
  line-height: 1.2;
}

.captured-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
  width: 100%;
  min-height: 32px;
}

.captured-piece {
  font-size: 1.3rem;
  line-height: 1;
  padding: 2px;
  background-color: white;
  border-radius: 3px;
  border: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.black-captured {
  border-color: #333;
  margin-top: 8px;
}

.white-captured {
  border-color: #888;
  margin-bottom: 8px;
}

.game-left-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  min-height: 0;
}

.game-right-column {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  min-height: 0;
  gap: 15px;
}

.game-controls-panel {
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.game-buttons {
  display: flex;
  gap: 10px;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.game-status {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  width: 100%;
}

.game-status p {
  margin: 0;
  font-size: 0.9rem;
  text-align: center;
}

.board-section {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  align-items: center;
}

.game-status {
  display: flex;
  gap: 20px;
  font-size: 1.1rem;
  font-weight: bold;
  align-items: center;
}

.status {
  color: #333;
  margin: 0;
}

.check-warning {
  color: #ff9800;
  margin: 0;
  font-size: 1rem;
}

.checkmate-warning {
  color: #e74c3c;
  margin: 0;
  font-size: 1.2rem;
}

.canvas-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
  touch-action: none;
  flex: 1;
  min-height: 0;
}

.game-canvas {
  border: 2px solid #333;
  background-color: #f0d9b5;
  cursor: pointer;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: block;
  max-width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
}

.btn {
  padding: 12px 24px;
  font-size: 1rem;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
  font-weight: bold;
  min-width: 100px;
}

.btn-primary {
  background-color: #27ae60;
  color: white;
}

.btn-primary:hover,
.btn-primary:active {
  background-color: #229954;
}

.btn-secondary {
  background-color: #3498db;
  color: white;
}

.btn-secondary:hover,
.btn-secondary:active {
  background-color: #2980b9;
}

.not-started-warning {
  color: #e67e22;
  margin: 0;
  font-size: 1rem;
  font-weight: bold;
}

.instructions {
  text-align: center;
  color: #7f8c8d;
  font-size: 0.85rem;
  margin: 0;
}

.instructions p {
  margin: 4px 0;
}

.moves-panel {
  width: 100%;
  border: 2px solid #ccc;
  border-radius: 8px;
  padding: 12px;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  box-sizing: border-box;
}

.moves-panel h3 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 1rem;
  flex-shrink: 0;
}

.moves-list {
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
  overflow-y: auto;
}

.move-entry {
  padding: 6px 8px;
  margin: 4px 0;
  background-color: white;
  border-left: 3px solid #3498db;
  font-size: 0.9rem;
  color: #333;
  display: flex;
  gap: 8px;
  align-items: center;
}

.move-number {
  font-weight: bold;
  color: #666;
  min-width: 28px;
}

.white-move {
  flex: 1;
  min-width: 40px;
}

.black-move {
  flex: 1;
  min-width: 40px;
  color: #666;
}

.clocks-panel {
  width: 100%;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
  box-sizing: border-box;
}

.clock {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 24px;
  border-radius: 8px;
  background-color: #f5f5f5;
  border: 2px solid #ccc;
  min-width: 120px;
  transition: all 0.3s;
}

.clock.active {
  border-color: #3498db;
  background-color: #e8f4f8;
  box-shadow: 0 2px 8px rgba(52, 152, 219, 0.3);
}

.white-clock.active {
  background-color: #f5f5f5;
  border-color: #333;
}

.black-clock.active {
  background-color: #333;
  border-color: #333;
}

.clock-label {
  margin: 0;
  font-size: 0.9rem;
  font-weight: bold;
  color: #333;
}

.black-clock.active .clock-label {
  color: #fff;
}

.clock-time {
  margin: 4px 0 0 0;
  font-size: 1.5rem;
  font-weight: bold;
  font-family: 'Courier New', monospace;
  color: #333;
}

.black-clock.active .clock-time {
  color: #fff;
}

@media (max-width: 1024px) {
  .game-container {
    flex-direction: column;
    align-items: center;
  }

  .game-right-column {
    width: 100%;
    min-height: 0;
  }

  .moves-panel {
    width: 100%;
    flex: 0 1 auto;
    min-height: auto;
    max-height: 300px;
  }
}

@media (max-width: 768px) {
  main {
    padding: 6px;
    padding-top: 70px;
  }

  .captured-section {
    padding: 6px 8px;
    min-height: 50px;
  }

  .captured-label {
    font-size: 0.65rem;
  }

  .captured-list {
    min-height: 28px;
  }

  .captured-piece {
    width: 24px;
    height: 24px;
    font-size: 1rem;
  }

  .game-left-column {
    gap: 10px;
  }

  .game-controls-panel {
    padding: 10px;
  }

  .game-buttons {
    gap: 8px;
  }

  .game-status {
    gap: 8px;
    font-size: 0.85rem;
  }

  .game-status p {
    font-size: 0.85rem;
  }

  .btn {
    padding: 10px 16px;
    font-size: 0.9rem;
    min-width: auto;
    width: 100%;
  }

  .game-canvas {
    max-width: 100%;
    width: 100%;
    max-height: 70vh;
    border: 3px solid #333;
  }

  .canvas-wrapper {
    padding: 4px;
    max-height: 75vh;
    overflow: auto;
  }

  .instructions {
    font-size: 0.8rem;
    padding: 0 10px;
  }

  .clocks-panel {
    flex-wrap: nowrap;
    gap: 10px;
  }

  .clock {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
  }

  .clock-label {
    font-size: 0.8rem;
  }

  .clock-time {
    font-size: 1.2rem;
  }
}

@media (max-width: 480px) {
  main {
    padding: 4px 4px 4px 4px;
    padding-top: 75px;
  }

  .game-canvas {
    max-width: 100%;
    width: 100%;
    max-height: 65vh;
    border: 3px solid #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    aspect-ratio: 1 / 1;
  }

  .canvas-wrapper {
    width: 100%;
    padding: 2px;
    max-height: 70vh;
    overflow: auto;
  }

  .game-buttons {
    width: 100%;
  }

  .btn {
    padding: 12px 16px;
    font-size: 0.9rem;
    min-width: 100%;
    width: 100%;
  }

  .instructions {
    font-size: 0.7rem;
    padding: 0 8px;
    line-height: 1.3;
  }

  .instructions p {
    margin: 2px 0;
  }
}

/* Phone landscape orientation: prevent vertical squishing */
@media (max-width: 768px) and (max-height: 500px) and (orientation: landscape) {
  main {
    padding-top: 50px;
  }

  h1 {
    font-size: 1rem;
    margin-bottom: 4px;
  }

  .game-left-column {
    gap: 8px;
  }

  .game-status {
    gap: 4px;
    font-size: 0.8rem;
  }

  .game-canvas {
    max-height: 60vh;
    width: 100%;
  }

  .canvas-wrapper {
    max-height: 65vh;
  }

  .btn {
    padding: 6px 12px;
    font-size: 0.85rem;
    min-width: 60px;
  }

  .instructions {
    font-size: 0.65rem;
    padding: 0 4px;
  }

  .instructions p {
    margin: 1px 0;
  }
}
</style>
